import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import DeleteIcon from '../../assets/images/icon-delete-button.svg';
import EditIcon from '../../assets/images/icon-edit-button.svg';

import { Checkbox, IntegrationItem, TextIconButton, UsersGridView, UsersListView } from '../../components/index';
import { IPaginatedListChangeEvent, IPaginatedListRef } from '../../components/PaginatedList/PaginatedList';
import { PageHeader } from '../../components/_mui';
import { UserIntegrationIcons } from '../../constants/integrations';
import { RowsPerPageEnum } from '../../constants/oneLink';
import { routes, URL_VARS } from '../../constants/routing';
import { INITIAL_PAGE } from '../../constants/table';
import { ADMIN_TYPE, CRM_SKIP_ALLOWED, DISTRIBUTOR } from '../../constants/users';
import { useHeaderDispatch } from '../../contexts/HeaderInfo';
import usePagination from '../../hooks/usePagination';
import useWindowSize from '../../hooks/useWindowSize';
import { editUserRequest } from '../../store/actions/users';
import { selectCurrentOrganizationData } from '../../store/selectors/organizations';
import { selectCanEditUser, selectUsers } from '../../store/selectors/users';
import { NotificationListEnum, ViewTypesEnum } from '../../types/shell';
import { IUser, TUserIntegrations, UserIntegrationStatusesEnum } from '../../types/users';
import { getFullName } from '../../utils/helpers';
import notification from '../../utils/notification';
import { handleApiError } from '../../utils/ui';
import { getDepartmentLabelById } from '../../utils/users';
import useFilters from './Filters/useFilters';

import styles from './UsersContainer.module.scss';

interface IProps {
  onDelete: (id: string) => void;
  checkCanEditUser: (user: Partial<IUser>) => boolean;
}

const UsersContainer = ({ onDelete, checkCanEditUser }: IProps) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { mobile } = useWindowSize();
  const { setHeader } = useHeaderDispatch();

  const userList = useSelector(selectUsers);
  const canEditUser = useSelector(selectCanEditUser);
  const { departmentList: departments, options: organizationOptions } =
    useSelector(selectCurrentOrganizationData) || {};

  const paginatedListRef = useRef<IPaginatedListRef<IUser>>(null);

  const handleAddClick = useCallback(() => {
    history.push(
      routes.users.getUserUrl({
        flowId: URL_VARS.NEW,
      }),
    );
  }, [history]);

  const { searchValue, filteredUsers, onClear, activeView, Filters } = useFilters({
    users: userList,
    canEdit: canEditUser,
    onAdd: handleAddClick,
  });

  const isSkipCrmAllowedInOrg = useMemo(() => organizationOptions?.crm_skip_allowed, [organizationOptions]);

  const {
    page,
    pageSize,
    currentItems: currentUsers,
    totalCount,
    handlePageChange,
    handleRowsPerPageChange,
  } = usePagination({
    items: filteredUsers,
    initialPage: 1,
    initialPageSize: RowsPerPageEnum.Fifty,
  });

  const handlePageChangeGridView = useCallback(
    ({ page: pageNumber, setPage }: IPaginatedListChangeEvent) => {
      handlePageChange(pageNumber);
      setPage?.(pageNumber);
    },
    [handlePageChange],
  );

  const handlePageSizeChangeGridView = useCallback(
    (newPageSize: number) => {
      handleRowsPerPageChange(newPageSize);
      if (paginatedListRef.current) {
        paginatedListRef.current.resetPageCount();
      }
    },
    [handleRowsPerPageChange],
  );

  const getDepartmentLabel = useCallback((id: string) => getDepartmentLabelById(id, departments), [departments]);

  const handleIntegrationClick = useCallback((name: string, uid?: string) => ({ name, uid }), []);

  const getIntegrationDetails = useCallback(
    (name: keyof TUserIntegrations) => {
      const { crm_currently_supports, email_currently_supports } = organizationOptions || {};

      const integrationName = (() => {
        switch (name) {
          case 'crm_integration':
            return crm_currently_supports;
          case 'email_integration':
            return email_currently_supports;
          default:
            break;
        }
      })();

      return {
        icon: integrationName ? UserIntegrationIcons[integrationName] : void 0,
        name: integrationName,
      };
    },
    [organizationOptions],
  );

  const renderIntegrations = useCallback(
    ({ integration, uid }: Partial<IUser>) =>
      integration ? (
        <div className={styles.integrationBar}>
          {Object.entries(integration).map(([name, status]) =>
            status !== UserIntegrationStatusesEnum.NA ? (
              <IntegrationItem
                key={name}
                name={getIntegrationDetails(name as keyof TUserIntegrations).name}
                active={status === UserIntegrationStatusesEnum.ON}
                icon={getIntegrationDetails(name as keyof TUserIntegrations).icon}
                className={styles.integrationItem}
                onClick={() => handleIntegrationClick(name, uid)}
              />
            ) : null,
          )}
        </div>
      ) : null,
    [getIntegrationDetails, handleIntegrationClick],
  );

  const handleCheckboxChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, user: IUser) => {
      const { checked } = event.currentTarget;
      return new Promise((resolve, reject) => {
        const { distributor_org_ids, crm_skip_allowed, ...rest } = user;
        const candidate = {
          ...(rest[ADMIN_TYPE] === DISTRIBUTOR ? { distributor_org_ids } : {}),
          crm_skip_allowed: checked,
          ...rest,
        };
        dispatch(
          editUserRequest(
            {
              user: candidate,
              resolve: () => {
                notification.success(NotificationListEnum.Success, {
                  content: `Skip CRM ${crm_skip_allowed ? 'disabled' : 'enabled'} for ${getFullName(
                    candidate.first_name,
                    candidate.last_name,
                  )}.`,
                });
                resolve(true);
              },
              reject: () => {
                reject();
                handleApiError(`Failed to update Skip CRM.`);
              },
            },
            { isSilent: true },
          ),
        );
      });
    },
    [dispatch],
  );

  const renderSkipCrmCheckbox = useCallback(
    (user: IUser) => {
      return (
        isSkipCrmAllowedInOrg && (
          <div className={styles.checkBox} onClick={(e) => e.stopPropagation()}>
            <Checkbox
              disabled={!checkCanEditUser(user)}
              checked={user[CRM_SKIP_ALLOWED] || false}
              onChange={(event) => handleCheckboxChange(event, user)}
              text="Allow Skip CRM"
            />
          </div>
        )
      );
    },
    [handleCheckboxChange, checkCanEditUser, isSkipCrmAllowedInOrg],
  );

  const renderControls = useCallback(
    (user: IUser) => (
      <React.Fragment>
        <TextIconButton
          trigger={mobile}
          className={styles.actionBtn}
          icon={EditIcon}
          title="Edit"
          onClick={(e) => {
            e.stopPropagation();
            history.push(
              routes.users.getUserUrl({
                flowId: URL_VARS.EDIT,
                userId: user.uid,
              }),
            );
          }}
        />
        <TextIconButton
          trigger={mobile}
          className={styles.actionBtn}
          icon={DeleteIcon}
          title="Delete"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(user.uid);
          }}
        />
        {canEditUser && renderSkipCrmCheckbox(user)}
      </React.Fragment>
    ),
    [mobile, onDelete, history, renderSkipCrmCheckbox, canEditUser],
  );

  const handleUserClick = useCallback(
    (user: IUser) => {
      history.push(
        routes.users.getUserUrl({
          flowId: checkCanEditUser(user) ? URL_VARS.EDIT : URL_VARS.VIEW,
          userId: user.uid,
        }),
      );
    },
    [history, checkCanEditUser],
  );

  useEffect(() => {
    if (setHeader) {
      setHeader({ title: 'Users', action: () => history.push(routes.dashboard) });
    }
  }, [history, setHeader]);

  const handleFiltersChange = useCallback(() => {
    handlePageChange(INITIAL_PAGE);
    paginatedListRef.current?.resetPageCount();
  }, [handlePageChange, paginatedListRef]);

  return (
    <div className={styles.container}>
      <PageHeader>
        <Filters onChange={handleFiltersChange} />
      </PageHeader>
      {activeView === ViewTypesEnum.List ? (
        <UsersListView
          users={currentUsers}
          getDepartmentLabel={getDepartmentLabel}
          handleRowClick={handleUserClick}
          renderIntegrations={renderIntegrations}
          searchQuery={searchValue}
          onDelete={onDelete}
          checkCanEditUser={checkCanEditUser}
          page={page}
          pageSize={pageSize}
          totalCount={totalCount}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          onClearFilters={onClear}
        />
      ) : (
        <UsersGridView
          ref={paginatedListRef}
          users={currentUsers}
          totalCount={totalCount}
          pageSize={pageSize}
          onChange={handlePageChangeGridView}
          onPageSizeChange={handlePageSizeChangeGridView}
          page={page}
          getDepartmentLabel={getDepartmentLabel}
          handleUserClick={handleUserClick}
          renderIntegrations={renderIntegrations}
          checkCanEditUser={checkCanEditUser}
          renderControls={renderControls}
          renderSkipCrmCheckbox={renderSkipCrmCheckbox}
          onClearFilters={onClear}
        />
      )}
    </div>
  );
};

export default UsersContainer;
